import { useEffect, useState } from 'react';
import axios from 'axios';
import { API_BASE_URL } from '../config/ApiConfig';
import { useNavigate } from 'react-router-dom';
import LoadingModal from './components/modals/LoadingModal';
import Pagination10 from './components/Pagination10';
import useDefaultModal from './components/hooks/useDefaultModal';
import useConfirmModal from './components/hooks/useConfirmModal';
import ConfirmModal from './components/modals/ConfirmModal';
import DefaultModal from './components/modals/DefaultModal';
import usePagination10 from './components/hooks/usePagination10';
import ElementoDespesaModal from './components/modalServices/ElementoDespesaModal';
import PageCard from './components/PageCard';
import usePermissions from './components/hooks/usePermissions';
function ElementoDespesas() {
  // Verificação de permissões
  const { podeAbrir, podeCriar, podeEditar, podeExcluir, podeExportar, carregando: carregandoPermissoes } = usePermissions('elemento_despesa');
  
  const [elementoDespesas, setElementoDespesas] = useState([]);
  const [atualizar, setAtualizar] = useState([]);
  const [isLoading, setLoading] = useState(true);
  const [showModal, setShowModal] = useState(false);
  const [selectedElementoDespesa, setSelectedElementoDespesa] = useState(null);
  const [selectedRecord, setSelectedRecord] = useState(null);
  const [filtroGeral, setFiltroGeral] = useState('');

  const navigate = useNavigate();
  const token = localStorage.getItem('token');
  const cnpj = localStorage.getItem('cnpj');

  // Filtrar dados
  const elementoDespesasFiltrados = [...elementoDespesas].filter(e => {
    if (!filtroGeral) return true;
    const termo = filtroGeral.trim().toLowerCase();
    const descricao = `${e.descricao || ''}`.toLowerCase();

    return descricao.includes(termo);
  });

  const totalItems = elementoDespesasFiltrados.length;
  const { setPagination } = usePagination10(totalItems);

  const selectedElementoDespesas = elementoDespesasFiltrados.slice(
    setPagination.startIndex,
    setPagination.startIndex + setPagination.rowsPerPage
  );

  const { setDefaultModal, showDefaultModal } = useDefaultModal();
  const { setConfirmModal, showConfirmModal } = useConfirmModal();

  useEffect(() => {
    if (!token) {
      navigate('/');
    }
    
    // Não carregar dados se não pode abrir o módulo
    if (!carregandoPermissoes && !podeAbrir) {
      return;
    }
    
    fetchElementoDespesas();
  }, [atualizar, podeAbrir, carregandoPermissoes]);

  function fetchElementoDespesas() {
    setLoading(true);
    axios
      .get(`${API_BASE_URL.ELEMENTO_DESPESA}/all`, {
        headers: {
          Authorization: `Bearer ${token}`,
        },
      })
      .then(result => {
        setElementoDespesas(result.data);
      })
      .catch(error => {
        console.error('Erro ao obter elementodespesas:', error);
        navigate('/');
      })
      .finally(() => {
        setLoading(false);
      });
  }

  const handleSelectRecord = record => {
    const recordKey = `${record.id}`;

    if (selectedRecord && selectedRecord.key === recordKey) {
      setSelectedRecord(null);
    } else {
      setSelectedRecord({ key: recordKey, data: record });
    }
  };

  const getSelectedRecord = () => {
    return selectedRecord ? selectedRecord.data : null;
  };

  const hasSelectedRecord = selectedRecord !== null;

  function handleDelete(elementodespesa) {
    if (!elementodespesa) return;

    axios
      .delete(`${API_BASE_URL.ELEMENTO_DESPESA}/delete/${elementodespesa.id}`, {
        headers: {
          Authorization: `Bearer ${token}`,
        },
      })
      .then(result => {
        setAtualizar(result);
        setSelectedRecord(null);
        showDefaultModal('success', 'O registro foi excluído com sucesso!');
      })
      .catch(error => {
        console.error('Erro ao excluir elementodespesa:', error);
        showDefaultModal('error', 'Erro ao excluir o registro!');
      });
  }

  const handleDeleteSelected = () => {
    const record = getSelectedRecord();
    if (record) {
      showConfirmModal('delete', () => handleDelete(record));
    }
  };

  const handleEditSelected = () => {
    const record = getSelectedRecord();
    if (record) {
      setSelectedElementoDespesa(record);
      setShowModal(true);
    }
  };

  function handleNewElementoDespesa() {
    setSelectedElementoDespesa(null);
    setShowModal(true);
  }

  function handleSuccess() {
    setAtualizar([]);
    setShowModal(false);
    setSelectedRecord(null);
  }

  // Verificar se ainda está carregando permissões
  if (carregandoPermissoes || isLoading) {
    return <LoadingModal loading={true} />;
  }

  return (
    <PageCard
      title="Elementos de Despesas"
      icon="bi-tags"
      scrollable={false}
      footer={!podeAbrir ? null : totalItems > 0 ? <Pagination10 {...setPagination} /> : null}
    >
      {!podeAbrir ? (
        <div className="alert alert-warning m-3">
          <i className="bi bi-exclamation-triangle me-2"></i>
          <strong>Acesso Negado</strong>
          <p className="mb-0 mt-2">
            Você não tem permissão para acessar o módulo de Elemento de Despesa.
          </p>
          <p className="mb-0 mt-2">
            <small>Entre em contato com o administrador do sistema.</small>
          </p>
          <button 
            className="btn btn-sm btn-outline-secondary mt-3"
            onClick={() => navigate('/home')}
          >
            <i className="bi bi-arrow-left me-1"></i>
            Voltar
          </button>
        </div>
      ) : (
      <div style={{ height: '100%', display: 'flex', flexDirection: 'column' }}>

        <div className="d-flex flex-wrap gap-1 mb-2 p-2 bg-light rounded border align-items-center">
          <button
            onClick={handleNewElementoDespesa}
            className="btn btn-success btn-sm d-inline-flex align-items-center"
            disabled={!podeCriar}
            title={!podeCriar ? 'Você não tem permissão para criar elementos de despesa' : 'Novo elemento de despesa'}
          >
            <i className="bi bi-plus-lg me-1" />
            Novo
          </button>
          <button
            className="btn btn-primary btn-sm d-inline-flex align-items-center"
            onClick={handleEditSelected}
            disabled={!podeEditar || !hasSelectedRecord}
            title={!podeEditar ? 'Você não tem permissão para editar' : !hasSelectedRecord ? 'Selecione um registro' : 'Editar registro selecionado'}
          >
            <i className="bi bi-pencil me-1" />
            Editar
          </button>
          <div className="vr mx-1" />
          <button
            className="btn btn-danger btn-sm d-inline-flex align-items-center"
            onClick={handleDeleteSelected}
            disabled={!podeExcluir || !hasSelectedRecord}
            title={!podeExcluir ? 'Você não tem permissão para excluir' : !hasSelectedRecord ? 'Selecione um registro' : 'Excluir registro selecionado'}
          >
            <i className="bi bi-trash me-1" />
            Excluir
          </button>

          <div className="ms-auto d-flex align-items-center">
            <div className="input-group" style={{ maxWidth: 300, height: 31 }}>
              <span className="input-group-text bg-white border-end-0 px-2" style={{ height: 31 }}>
                <i className="bi bi-search text-muted" style={{ fontSize: 13 }} />
              </span>
              <input
                type="text"
                className="form-control form-control-sm border-start-0"
                style={{ textTransform: 'uppercase', height: 31, fontSize: 13 }}
                placeholder="Pesquisar..."
                value={filtroGeral}
                onChange={e => setFiltroGeral(e.target.value.toUpperCase())}
              />
              {filtroGeral && (
                <button
                  className="btn btn-outline-secondary btn-sm border-start-0 d-flex align-items-center px-2"
                  style={{ height: 31 }}
                  onClick={() => setFiltroGeral('')}
                  title="Limpar pesquisa"
                  tabIndex={-1}
                >
                  <i className="bi bi-x-lg" style={{ fontSize: 11 }} />
                </button>
              )}
            </div>
          </div>
        </div>

        <div
          className="table-responsive-sm shadow-sm mb-0 small"
          style={{ flex: 1, minHeight: 0, overflowY: 'auto' }}
        >
          <table className="table table-hover table-striped table-sm">
            <thead className="table-secondary sticky-top bg-light">
              <tr>
                <th className="px-2 align-middle" style={{ width: '4%' }}>
                  ID
                </th>
                <th className="px-2 align-middle">Nome</th>
              </tr>
            </thead>
            <tbody>
              {!isLoading &&
                selectedElementoDespesas.map(p => {
                  const recordKey = `${p.id}`;
                  const isSelected =
                    selectedRecord && selectedRecord.key === recordKey;

                  let rowClass = '';
                  let rowStyle = {
                    cursor: 'pointer',
                  };

                  if (isSelected) {
                    rowClass = 'table-active border-primary border-2';
                    rowStyle.backgroundColor = '#e3f2fd';
                    rowStyle.borderLeft = '4px solid #0d6efd';
                    rowStyle.boxShadow = '0 2px 4px rgba(13, 110, 253, 0.2)';
                  }

                  return (
                    <tr
                      key={recordKey}
                      className={rowClass}
                      style={rowStyle}
                      onClick={() => handleSelectRecord(p)}
                    >
                      <td className="px-2">{p.id}</td>
                      <td className="px-2">{p.descricao}</td>
                    </tr>
                  );
                })}
              {!isLoading && elementoDespesasFiltrados.length === 0 && (
                <tr>
                  <td colSpan="2" className="text-center">
                    Nenhum registro encontrado.
                  </td>
                </tr>
              )}
            </tbody>
          </table>
        </div>
      </div>
      )}

      <ConfirmModal {...setConfirmModal} />
      <DefaultModal {...setDefaultModal} />
      <ElementoDespesaModal
        show={showModal}
        onHide={() => setShowModal(false)}
        elementodespesa={selectedElementoDespesa}
        onSuccess={handleSuccess}
      />
    </PageCard>
  );
}

export default ElementoDespesas;
